import { z } from "zod";
import { providerProductAreaSchema, productAreaCensusSchema } from "@/lib/tenant-inventory-contract";
export { providerProductAreaSchema, productAreaCensusSchema };

const entitlementSchema = z.object({
  status: z.enum(["contracted", "not_contracted", "suspended"]),
  source: z.string().min(1),
  effective_from: z.string().datetime().optional(),
  effective_until: z.string().datetime().optional(),
  version: z.number().int().positive(),
  changed_by: z.string().optional(),
  changed_at: z.string().datetime(),
});

// Setup is a separate axis from activation. An area can be in progress while
// activation stays off; the two must never be collapsed into one status.
export const setupStateSchema = z.enum(["not_started", "in_progress"]);

/**
 * What an area's switch does to sign-in and access at runtime.
 *
 * legacy_unchanged: the switch leaves runtime behavior unchanged.
 * area_gates_offering: retained for older combined-Frontline responses.
 * enforced: runtime entry points wired to the server gate refuse while the area
 * is not on. observed: those entry points record where they would refuse, the
 * step before enforced.
 *
 * Every reader of a product-area decision shares this, wherever the decision
 * reaches them. A reader that pinned one value treated every other as a
 * malformed response — see the applications workspace capabilities.
 */
export const runtimeEffectSchema = z.enum([
  "legacy_unchanged",
  "area_gates_offering",
  "enforced",
  "observed",
]);

const stateSchema = z.object({
  activation: z.enum(["off", "setup", "on"]),
  setup: setupStateSchema,
  enforcement: z.enum(["not_applicable", "off", "observe", "enforce"]),
  owner_subject: z.string().optional(),
  support_contact: z.string().optional(),
  config_generation: z.number().int().positive(),
  version: z.number().int().positive(),
  changed_by: z.string().optional(),
  changed_at: z.string().datetime(),
});

// In lockstep with productarea.Areas() in backend/internal/tenant/productarea;
// TestConsoleKnowsEveryProductArea there holds the two together, because an area
// missing here fails the whole inventory parse and every area gate reads unknown.
export const productAreaIdSchema = z.enum([
  "passwordless", "application_sign_in", "identity_governance", "private_access",
  "privileged_access", "badge_sign_in", "kiosk_pass", "non_human_identity", "windows_sign_in", "compliance",
  "directory",
]);

const productAreaBaseSchema = z.object({
  area: productAreaIdSchema,
  compatible: z.boolean(),
  entitlement: entitlementSchema.nullable(),
  state: stateSchema.nullable(),
  readiness: z.object({
    status: z.enum(["unknown", "ready", "needs_attention"]),
    reason_code: z.string(),
    observed_at: z.string().datetime().optional(),
  }),
  display_status: z.enum(["unresolved", "unavailable", "off", "setup_required", "on", "suspended"]),
  reason_code: z.string(),
  actions: z.array(z.object({
    id: z.literal("start_setup"),
    destination: z.literal("product_area_setup"),
  })),
  runtime_effect: runtimeEffectSchema,
  // The connection fact: whether the area has configuration
  // behind it now.
  retained_configuration: z.boolean(),
});

// The tenant list carries every area since the console's feature
// registry gates every place on it. An area this client does not know is
// still rejected, so a newer server cannot make the roster render a row the
// copy table has no words for.
export const productAreaSchema = productAreaBaseSchema;

export const productAreaListSchema = z.object({ data: z.array(productAreaSchema) });
export const entitlementResponseSchema = entitlementSchema;

export const automatedOnboardingCapabilitySchema = z.object({
  data: z.object({
    capability: z.literal("automated_onboarding"),
    availability: z.enum(["hidden", "read_only", "manage"]),
    reason_code: z.string().min(1),
    runtime_effect: runtimeEffectSchema,
  }),
});

export const applicationCreationCapabilitySchema = z.object({
  data: z.object({
    capability: z.literal("application_creation"),
    availability: z.enum(["hidden", "read_only", "manage"]),
    reason_code: z.string().min(1),
    runtime_effect: runtimeEffectSchema,
  }),
});

// The preview is the server's decision plus the facts behind it. The client
// renders it and never re-derives eligibility: an unrecognized blocker must
// still block, so blockers stay open strings rather than a client-side enum.
export const setupPreviewSchema = z.object({
  area: productAreaIdSchema,
  inventory: productAreaBaseSchema,
  eligible: z.boolean(),
  blockers: z.array(z.string()),
  requirements: z.array(z.string()),
  expected_version: z.number().int().nonnegative(),
  entitlement_version: z.number().int().nonnegative(),
  operation_ref: z.string().min(1),
  checked_at: z.string().datetime(),
});

// The activation preview is the server's decision to switch an area on or
// off plus the facts behind it; the client renders it and never re-derives
// eligibility (same rule as the setup preview).
export const activationPreviewSchema = z.object({
  area: productAreaIdSchema,
  to: z.enum(["on", "off"]),
  inventory: productAreaBaseSchema,
  eligible: z.boolean(),
  blockers: z.array(z.string()),
  retained_configuration: z.boolean(),
  // What the transition does to sign-in and access; the confirmation's words
  // follow it.
  effect: z.enum(["none", "frontline_sign_in_stops", "area_stops"]),
  expected_version: z.number().int().nonnegative(),
  entitlement_version: z.number().int().nonnegative(),
  operation_ref: z.string().min(1),
  checked_at: z.string().datetime(),
});

export const setupTransitionSchema = z.object({
  area: productAreaIdSchema,
  operation_ref: z.string().min(1),
  cleared_offerings: z.array(z.string()).optional(),
  before: stateSchema,
  after: stateSchema,
  replayed: z.boolean(),
});

export type ProductArea = z.infer<typeof productAreaSchema>;
export type ProviderProductArea = z.infer<typeof providerProductAreaSchema>;
export type EntitlementResponse = z.infer<typeof entitlementResponseSchema>;
export type SetupPreview = z.infer<typeof setupPreviewSchema>;
export type ActivationPreview = z.infer<typeof activationPreviewSchema>;
export type SetupTransition = z.infer<typeof setupTransitionSchema>;
export type ProductAreaState = z.infer<typeof stateSchema>;
